import { useUserStore } from '@/stores';
import { useAnswerDateStore } from '@/stores/answer_date';
import { getSessionExpiredRedirectPath } from '@/utils/sessionRedirect';
import dayjs from 'dayjs';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

const TheAuthContainer = (): Promise<typeof import('@/containers/TheAuthContainer.vue')> =>
  import('@/containers/TheAuthContainer.vue');
const Login = (): Promise<typeof import('@/views/auth/Login.vue')> => import('@/views/auth/Login.vue');
const TwoFactorVerification = (): Promise<typeof import('@/views/auth/TwoFactorVerification.vue')> =>
  import('@/views/auth/TwoFactorVerification.vue');
const ForgetPassword = (): Promise<typeof import('@/views/auth/ForgetPassword.vue')> =>
  import('@/views/auth/ForgetPassword.vue');
const ResetPassword = (): Promise<typeof import('@/views/auth/ResetPassword.vue')> =>
  import('@/views/auth/ResetPassword.vue');
const TheContainer = (): Promise<typeof import('@/containers/TheContainer.vue')> =>
  import('@/containers/TheContainer.vue');
const HomeContainer = (): Promise<typeof import('@/containers/HomeContainer.vue')> =>
  import('@/containers/HomeContainer.vue');
const Index = (): Promise<typeof import('@/views/Index.vue')> => import('@/views/Index.vue');
const QuestionStart = (): Promise<typeof import('@/views/QuestionStart.vue')> => import('@/views/QuestionStart.vue');
const QuestionStartPreview = (): Promise<typeof import('@/views/QuestionStartPreview.vue')> =>
  import('@/views/QuestionStartPreview.vue');
const QuestionItem = (): Promise<typeof import('@/views/QuestionItem.vue')> => import('@/views/QuestionItem.vue');
const QuestionItemByGroup = (): Promise<typeof import('@/views/QuestionItemByGroup.vue')> =>
  import('@/views/QuestionItemByGroup.vue');
const QuestionConfirm = (): Promise<typeof import('@/views/QuestionConfirm.vue')> =>
  import('@/views/QuestionConfirm.vue');
const QuestionComplete = (): Promise<typeof import('@/views/QuestionComplete.vue')> =>
  import('@/views/QuestionComplete.vue');
const QuestionCompletePreview = (): Promise<typeof import('@/views/QuestionCompletePreview.vue')> =>
  import('@/views/QuestionCompletePreview.vue');
const AnswerEdit = (): Promise<typeof import('@/views/AnswerEdit.vue')> =>
  import('@/views/AnswerEdit.vue');
const PasswordSetting = (): Promise<typeof import('@/views/auth/PasswordSetting.vue')> =>
  import('@/views/auth/PasswordSetting.vue');
const AutoSignIn = (): Promise<typeof import('@/views/auth/AutoSignIn.vue')> =>
  import('@/views/auth/AutoSignIn.vue');
const LoginWebSurvey = (): Promise<typeof import('@/views/auth/LoginWebSurvey.vue')> =>
  import('@/views/auth/LoginWebSurvey.vue');
const EclinroSignIn = (): Promise<typeof import('@/views/auth/EclinroSignIn.vue')> =>
  import('@/views/auth/EclinroSignIn.vue');
const PageNotFound = (): Promise<typeof import('@/views/PageNotFound.vue')> => import('@/views/PageNotFound.vue');
const SessionExpired = (): Promise<typeof import('@/views/auth/SessionExpired.vue')> =>
  import('@/views/auth/SessionExpired.vue');
const SessionLogin = (): Promise<typeof import('@/components/SessionLogin.vue')> => import('@/components/SessionLogin.vue');
const EmailRegistration = (): Promise<typeof import('@/views/EmailRegistration.vue')> => import('@/views/EmailRegistration.vue');

const Home = (): Promise<typeof import('@/views/Home.vue')> =>
  import('@/views/Home.vue');

const routes: Array<RouteRecordRaw> = [
  {
    path: '/auth',
    component: TheAuthContainer,
    children: [
      // ログイン画面
      {
        path: 'login',
        name: 'Login',
        component: Login,
      },
      {
        path: '/',
        redirect: 'login',
      },
      // skipCacheReload: TheAuthContainer.mounted()のlocation.reload()をスキップし、
      // fullReset()後のPiniaストア(two_factor_data等)消失によるリロードループを防止する
      {
        path: 'two-factor',
        name: 'TwoFactorVerification',
        component: TwoFactorVerification,
        meta: { skipCacheReload: true },
      },
      {
        path: 'password/send-email',
        name: 'ForgetPassword',
        component: ForgetPassword,
      },
      {
        path: 'password/reset',
        name: 'ResetPassword',
        component: ResetPassword,
      },
      {
        path: 'session-login',
        name: 'SessionLogin',
        component: SessionLogin,
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    // プレビュー用
    path: '/preview',
    component: TheContainer,
    children: [
      // 各proの回答開始画面
      {
        path: ':uuid/start',
        name: 'QuestionStartPreview',
        component: QuestionStartPreview,
      },
      // 各質問項目（質問グループ、質問のnoは数え番号でidや配列のindexではない）
      {
        path: 'questionnaire/:uuid/:question_group_no/:question_no',
        name: 'QuestionItemPreview',
        component: QuestionItem,
      },
      // 質問群の全質問項目（質問グループ、質問のnoは数え番号でidや配列のindexではない）
      {
        path: 'questionnaire-by-group/:uuid/:question_group_no',
        name: 'QuestionItemByGroupPreview',
        component: QuestionItemByGroup,
      },
      // proごとの回答確認画面
      {
        path: 'questionnaire/:uuid/confirm',
        name: 'QuestionConfirmPreview',
        component: QuestionConfirm,
      },
      // proごとの回答完了画面
      {
        path: 'questionnaire/:uuid/complete',
        name: 'QuestionCompletePreview',
        component: QuestionCompletePreview,
      },
    ],
  },
  {
    // eClinRO用
    path: '/eclinro',
    component: TheContainer,
    children: [
      // eClinRO利用時のログイン処理
      // skipCacheReload: 初回アクセス時（last_view_update=0）にTheContainer.mounted()のlocation.reload()をスキップし、
      // サインインAPIとのレースコンディションを防止する（AutoSignInのskipCacheReload追加と同パターン）
      {
        path: 'sign-in',
        name: 'EclinroSignIn',
        component: EclinroSignIn,
        meta: { skipCacheReload: true },
      },
      // 各proの回答開始画面
      {
        path: 'answer_date/:answer_date_id/start',
        name: 'QuestionStartEclinro',
        component: QuestionStart,
        meta: { requiresAuth: true },
      },
      // proごとの回答修正画面
      {
        path: 'answer_date/:answer_date_id/edit',
        name: 'AnswerEditEclinro',
        component: AnswerEdit,
        meta: { requiresAuth: true },
      },
      // 各質問項目（質問グループ、質問のnoは数え番号でidや配列のindexではない）
      {
        path: 'questionnaire/:questionnaire_id/:question_group_no/:question_no',
        name: 'QuestionItemEclinro',
        component: QuestionItem,
        meta: { requiresAuth: true },
      },
      // 質問群の全質問項目（質問グループ、質問のnoは数え番号でidや配列のindexではない）
      {
        path: 'questionnaire-by-group/:questionnaire_id/:question_group_no',
        name: 'QuestionItemByGroupEclinro',
        component: QuestionItemByGroup,
        meta: { requiresAuth: true },
      },
      // proごとの回答確認画面
      {
        path: 'questionnaire/:questionnaire_id/confirm',
        name: 'QuestionConfirmEclinro',
        component: QuestionConfirm,
        meta: { requiresAuth: true },
      },
      // proごとの回答完了画面
      {
        path: 'questionnaire/:questionnaire_id/complete',
        name: 'QuestionCompleteEclinro',
        component: QuestionComplete,
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    path: '/',
    component: TheContainer,
    children: [
      // 日毎の質問紙がボタンで選択できる
      {
        path: '/index',
        name: 'Index',
        component: Index,
        meta: { requiresAuth: true },
      },
      {
        path: '/',
        redirect: '/home',
      },
      // 各proの回答開始画面
      // skipCacheReload: WebSurvey 2FA完了後の遷移時にTheContainer.mounted()のlocation.reload()をスキップし、
      // fullReset()後のlast_view_update=0によるAPIリクエスト中断（エラーダイアログ表示）を防止する
      {
        path: '/answer_date/:answer_date_id/start',
        name: 'QuestionStart',
        component: QuestionStart,
        meta: { requiresAuth: true, skipCacheReload: true },
      },
      // 各随時回答タイプproの回答開始画面
      // skipCacheReload: 同上（WebSurvey 2FA後のレースコンディション防止）
      {
        path: '/anytime/:questionnaire_id/start',
        name: 'AnytimeQuestionStart',
        component: QuestionStart,
        meta: { requiresAuth: true, skipCacheReload: true },
      },
      // 各質問項目（質問グループ、質問のnoは数え番号でidや配列のindexではない）
      {
        path: '/questionnaire/:questionnaire_id/:question_group_no/:question_no',
        name: 'QuestionItem',
        component: QuestionItem,
        meta: { requiresAuth: true },
      },
      // 質問群の全質問項目（質問グループ、質問のnoは数え番号でidや配列のindexではない）
      {
        path: '/questionnaire-by-group/:questionnaire_id/:question_group_no',
        name: 'QuestionItemByGroup',
        component: QuestionItemByGroup,
        meta: { requiresAuth: true },
      },
      // proごとの回答確認画面
      {
        path: '/questionnaire/:questionnaire_id/confirm',
        name: 'QuestionConfirm',
        component: QuestionConfirm,
        meta: { requiresAuth: true },
      },
      // proごとの回答完了画面
      {
        path: '/questionnaire/:questionnaire_id/complete',
        name: 'QuestionComplete',
        component: QuestionComplete,
        meta: { requiresAuth: true },
      },
      // proごとの回答修正画面
      {
        path: '/answer_date/:answer_date_id/edit',
        name: 'AnswerEdit',
        component: AnswerEdit,
        meta: { requiresAuth: true },
      },
      {
        path: '/password/setting',
        name: 'PasswordSetting',
        component: PasswordSetting,
        meta: { requiresAuth: true },
      },
      // リンクからAPIを実行させて自動ログインさせる
      // skipCacheReload: TheContainer.mounted()のlocation.reload()をスキップし、
      // autoSignIn APIとのレースコンディションによるユーザー二重作成を防止する
      {
        path: '/forms/:uuid',
        name: 'AutoSignIn',
        component: AutoSignIn,
        meta: { skipCacheReload: true },
      },
      // 登録済み被験者のURLから直接ログイン、質問紙回答画面へ遷移
      {
        path: '/login-websurvey',
        name: 'LoginWebSurvey',
        component: LoginWebSurvey,
        meta: { skipCacheReload: true },
      },
      // セッション期限切れページ（WebSurvey/回答リンク経由ユーザー用）
      {
        path: '/session-expired',
        name: 'SessionExpired',
        component: SessionExpired,
        meta: { skipCacheReload: true },
      },
      // 404ページ
      {
        path: '/page-not-found',
        alias: '/:pathMatch(.*)*',
        name: 'PageNotFound',
        component: PageNotFound,
      },
    ],
  },
  {
    path: '/',
    component: HomeContainer,
    children: [
      {
        path: '/home',
        name: 'Home',
        component: Home,
        meta: { requiresAuth: true },
      },
      {
        path: '/email-registration',
        name: 'EmailRegistration',
        component: EmailRegistration,
        meta: { requiresAuth: true },
      },
    ]
  }
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  linkActiveClass: 'active',
  scrollBehavior(to, from, savedPosition) {
    return { left: 0, top: 0 };
  },
  routes,
});

// 認証を入れる
router.beforeEach((to, from, next) => {
  const store = useUserStore();

  // WebSurveyフラグのリセット: /index または /home に遷移する場合
  if (to.path === '/index' || to.path === '/home') {
    store.is_websurvey = false;
  }

  // 回答リンク（autoSignIn）経由のアクセス時は、質問紙回答フロー以外の画面への遷移を制限する
  const answerDateStore = useAnswerDateStore();
  if (answerDateStore.is_auto_sign_in) {
    const allowedPathPrefixes = [
      '/forms/',
      '/anytime/',
      '/answer_date/',
      '/questionnaire/',
      '/questionnaire-by-group/',
      '/email-registration',
      '/page-not-found',
      '/session-expired',  // セッション期限切れ画面への遷移を許可
      '/auth/',  // セッション期限切れ時のログイン画面遷移を許可（無限リダイレクトループ防止）
      '/preview/',  // 質問紙プレビューへの遷移を許可（client/medical画面からのリダイレクト）
    ];
    const isAllowed = allowedPathPrefixes.some((prefix) => to.path.startsWith(prefix));
    if (!isAllowed) {
      // 質問紙回答フロー外への遷移を防止し、アンケート開始画面にリダイレクトする
      const redirectPath = answerDateStore.auto_sign_in_redirect_path;
      if (redirectPath) {
        next({ path: redirectPath, replace: true });
      } else {
        next({ path: '/page-not-found', replace: true });
      }
      return;
    }
  }

  if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    store.api_token == null
  ) {
    // 認証必要ページでtokenがなければリダイレクト
    // WebSurvey/回答リンク経由ユーザーはセッション期限切れ画面に遷移させる
    // ※ is_websurveyはpersist:trueでLocalStorageに残るため、ブックマーク等からの
    //   再訪問時にも/session-expiredに飛ぶ可能性がある（意図した動作）
    const redirectPath = getSessionExpiredRedirectPath();
    next(typeof redirectPath === 'string' ? { path: redirectPath } : redirectPath);
  } else if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    store.api_token != null &&
    !store.remember &&
    dayjs().unix() > store.limit
  ) {
    // 認証必要ページでtokenがあっても期限切れならリダイレクト
    // WebSurvey/回答リンク経由ユーザーはセッション期限切れ画面に遷移させる
    const redirectPath = getSessionExpiredRedirectPath();
    store.$reset();
    // answerDateStoreは元コードでもリセットしていないため、ここではリセットしない
    // （getSessionExpiredRedirectPathでis_auto_sign_inを取得済みのため不要）
    next(typeof redirectPath === 'string' ? { path: redirectPath } : redirectPath);
  } else if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    store.api_token != null &&
    !store.remember
  ) {
    // 認証必要ページでtokenが有効なら，期限を伸ばす
    const tokenLifetimeMinutes = parseInt(import.meta.env.VITE_APP_API_TOKEN_LIFETIME_MINUTES || '1440');
    const limit = dayjs().add(tokenLifetimeMinutes, 'minutes').unix();
    store.limit = limit;
    next();
  } else {
    // その他の場合はそのまま表示
    next();
  }
});

// チャンク/CSSの読み込み失敗時に1回だけリロードする
// iOS Safariで通信遅延時にCSSプリロードが失敗すると、モジュールインポート全体が失敗する問題への対策
const CHUNK_LOAD_FAILED_KEY = 'chunk_load_failed';

// エラーパターンの定数化
const CHUNK_LOAD_ERROR_PATTERNS = [
  'Loading chunk',
  'Loading CSS chunk',
  'Unable to preload CSS',
  'Importing a module script failed',
  'Failed to fetch dynamically imported module',
] as const;

// sessionStorageが使用できない環境への対応
const canUseSessionStorage = (): boolean => {
  try {
    sessionStorage.setItem('test', 'test');
    sessionStorage.removeItem('test');
    return true;
  } catch {
    return false;
  }
};

router.onError((error) => {
  const isChunkLoadError = CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => error.message.includes(pattern));

  if (isChunkLoadError) {
    // デバッグのためにエラー情報をログ出力
    console.warn('Chunk load error detected:', error.message, 'at', window.location.pathname);

    if (!canUseSessionStorage()) {
      // sessionStorageが使用できない場合は単純にリロード
      window.location.reload();
      return;
    }

    const lastFailedPath = sessionStorage.getItem(CHUNK_LOAD_FAILED_KEY);
    const currentPath = window.location.pathname;

    // 同じパスで連続してエラーが発生した場合は無限ループを防ぐためリロードしない
    if (lastFailedPath !== currentPath) {
      sessionStorage.setItem(CHUNK_LOAD_FAILED_KEY, currentPath);
      window.location.reload();
    } else {
      // 2回目の失敗時はフラグをクリアして、次回のナビゲーションでリトライできるようにする
      sessionStorage.removeItem(CHUNK_LOAD_FAILED_KEY);
    }
  }
});

// ナビゲーション成功時にフラグをクリア
router.afterEach(() => {
  if (canUseSessionStorage()) {
    sessionStorage.removeItem(CHUNK_LOAD_FAILED_KEY);
  }
});

export default router;
